import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAITaskRegistration } from "../lib/aiTasks";
import { Plus, Trash, FloppyDisk, Sparkle, DownloadSimple, X, ImageSquare, ArrowsClockwise, Brain, PencilSimple, Check } from "@phosphor-icons/react";

/**
 * Reusable CV editor. Receives the coachee_id and handles all CRUD + photo
 * upload + AI analysis + .docx download. Used by both:
 *  - Coachee on /cv (their own CV)
 *  - Work coach on coachee detail page (CV tab)
 */
export default function CVEditor({ coacheeId, readOnly = false }) {
  const [cv, setCv] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null); // blob URL
  const [templates, setTemplates] = useState([]);
  const [brainTypeLabel, setBrainTypeLabel] = useState("");
  const [brainTypeBlurb, setBrainTypeBlurb] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [editingAI, setEditingAI] = useState(false);
  const fileInput = useRef(null);
  const pollTimerRef = useRef(null);
  const photoBlobRef = useRef(null);

  // Global AI-task pill: shows a persistent status pill in the bottom-right
  // even if the coachee navigates away from /cv while the AI feedback is
  // being generated.
  useAITaskRegistration({
    active: analyzing,
    id: coacheeId ? `cv-analyze-${coacheeId}` : null,
    label: "CV-feedback genereren",
    returnTo: "/cv",
  });

  // Convert the photo endpoint to a blob URL with credentials. Required
  // because <img src="..."> cross-origin requests do NOT send cookies by
  // default and the Cloudflare proxy occasionally strips credentials.
  const refreshPhoto = async () => {
    try {
      const resp = await api.get(`/coachees/${coacheeId}/cv/photo`, { responseType: "blob" });
      if (photoBlobRef.current) URL.revokeObjectURL(photoBlobRef.current);
      const url = URL.createObjectURL(resp.data);
      photoBlobRef.current = url;
      setPhotoUrl(url);
    } catch (err) {
      // 404 = no photo yet; that's fine
      if (err?.response?.status !== 404) {
        console.warn("Photo fetch failed:", err?.message || err);
      }
      setPhotoUrl(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: tmpl }, { data }] = await Promise.all([
        api.get("/cv-templates"),
        api.get(`/coachees/${coacheeId}/cv`),
      ]);
      setTemplates(tmpl);
      setCv(data.cv);
      setBrainTypeLabel(data.brain_type_label || "");
      setBrainTypeBlurb(data.brain_type_blurb || "");
      if (data.photo_url) {
        await refreshPhoto();
      } else {
        setPhotoUrl(null);
      }
      // If a previous analysis was still running, resume polling.
      if (data.ai_status === "running") {
        setAnalyzing(true);
        startPolling();
      } else if (data.ai_status === "error" && data.ai_error) {
        setError(`AI-analyse fout: ${data.ai_error}`);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); return () => stopPolling(); /* eslint-disable-next-line */ }, [coacheeId]);
  useEffect(() => () => { if (photoBlobRef.current) URL.revokeObjectURL(photoBlobRef.current); }, []);

  // -------- helpers --------
  const update = (patch) => setCv((c) => ({ ...c, ...patch }));
  const updatePI = (patch) => setCv((c) => ({ ...c, personal_info: { ...c.personal_info, ...patch } }));
  const updateList = (key, idx, patch) =>
    setCv((c) => ({ ...c, [key]: c[key].map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = (key, blank) => setCv((c) => ({ ...c, [key]: [...c[key], blank] }));
  const removeItem = (key, idx) => setCv((c) => ({ ...c, [key]: c[key].filter((_, i) => i !== idx) }));

  const updateAch = (wIdx, aIdx, patch) =>
    setCv((c) => ({
      ...c,
      work_experience: c.work_experience.map((w, i) =>
        i === wIdx
          ? { ...w, achievements: (w.achievements || []).map((a, j) => (j === aIdx ? { ...a, ...patch } : a)) }
          : w
      ),
    }));
  const addAch = (wIdx) =>
    updateList("work_experience", wIdx, {
      achievements: [
        ...((cv.work_experience[wIdx].achievements) || []),
        { situation: "", task: "", action: "", result: "", rendered: "" },
      ],
    });
  const removeAch = (wIdx, aIdx) =>
    setCv((c) => ({
      ...c,
      work_experience: c.work_experience.map((w, i) =>
        i === wIdx ? { ...w, achievements: (w.achievements || []).filter((_, j) => j !== aIdx) } : w
      ),
    }));

  // -------- actions --------
  const save = async () => {
    setError(""); setSaving(true);
    try {
      await api.put(`/coachees/${coacheeId}/cv`, cv);
      setSavedAt(new Date());
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const onPhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/coachees/${coacheeId}/cv/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshPhoto();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    e.target.value = "";
  };
  const removePhoto = async () => {
    if (!window.confirm("Foto verwijderen?")) return;
    await api.delete(`/coachees/${coacheeId}/cv/photo`);
    if (photoBlobRef.current) {
      URL.revokeObjectURL(photoBlobRef.current);
      photoBlobRef.current = null;
    }
    setPhotoUrl(null);
  };

  // -------- AI polling --------
  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };
  const startPolling = () => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/coachees/${coacheeId}/cv`);
        if (data.ai_status === "done") {
          setCv((c) => ({ ...c, ai_feedback: data.cv?.ai_feedback || "" }));
          setSavedAt(new Date());
          setAnalyzing(false);
          stopPolling();
        } else if (data.ai_status === "error") {
          setError(`AI-analyse fout: ${data.ai_error || "onbekende fout"}`);
          setAnalyzing(false);
          stopPolling();
        }
      } catch (err) {
        console.warn("AI poll failed:", err?.message || err);
      }
    }, 3000);
  };

  const runAI = async () => {
    setAnalyzing(true); setError("");
    try {
      // Auto-save first so the AI sees the latest content.
      await api.put(`/coachees/${coacheeId}/cv`, cv);
      await api.post(`/coachees/${coacheeId}/cv/ai-analyze`);
      startPolling();
    } catch (err) {
      setAnalyzing(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const download = async () => {
    setError("");
    try {
      await api.put(`/coachees/${coacheeId}/cv`, cv);
      const resp = await api.get(`/coachees/${coacheeId}/cv/download`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CV - ${cv.personal_info?.name || "coachee"}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (loading || !cv) return <div className="text-zinc-500">CV laden...</div>;

  return (
    <div className="space-y-8" data-testid="cv-editor">
      {/* Sticky action bar */}
      <div className="sticky top-0 z-20 -mx-8 px-8 py-3 bg-white/95 backdrop-blur border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Template</label>
          <select
            value={cv.template_id}
            onChange={(e) => update({ template_id: e.target.value })}
            data-testid="cv-template-select"
            className="border border-zinc-300 px-2 py-1 text-sm rounded-sm bg-white"
            disabled={readOnly}
          >
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && (
            <span className="text-xs text-zinc-400" data-testid="cv-saved-at">
              Opgeslagen om {savedAt.toLocaleTimeString()}
            </span>
          )}
          {!readOnly && (
            <button onClick={save} disabled={saving} data-testid="cv-save-button" className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:opacity-60">
              <FloppyDisk size={16} /> {saving ? "Opslaan..." : "Opslaan"}
            </button>
          )}
          <button onClick={download} data-testid="cv-download-button" className="inline-flex items-center gap-2 bg-[#43A047] text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#388E3C] transition-colors">
            <DownloadSimple size={16} /> .docx
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-[#E53935] border border-[#E53935]/30 bg-[#E53935]/5 px-3 py-2 rounded-sm" data-testid="cv-error">{error}</div>}

      {/* Photo + Personal info side by side */}
      <SectionCard title="Persoonsgegevens" testid="cv-section-personal">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6">
          <div className="flex flex-col items-center" data-testid="cv-photo-block">
            <div className="w-40 h-40 bg-zinc-100 border-2 border-dashed border-zinc-300 rounded-sm overflow-hidden flex items-center justify-center mb-3">
              {photoUrl ? (
                <img src={photoUrl} alt="Pasfoto" className="w-full h-full object-cover" data-testid="cv-photo-preview" />
              ) : (
                <ImageSquare size={48} className="text-zinc-300" />
              )}
            </div>
            <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhotoChange} className="hidden" data-testid="cv-photo-input" />
            <div className="flex flex-col items-center gap-1.5">
              <button
                onClick={() => fileInput.current?.click()}
                data-testid="cv-photo-upload-button"
                disabled={readOnly}
                className="text-xs uppercase tracking-widest border border-zinc-300 px-3 py-1.5 hover:border-zinc-900 transition-colors rounded-sm"
              >
                {photoUrl ? "Vervang foto" : "Upload foto"}
              </button>
              {photoUrl && !readOnly && (
                <button onClick={removePhoto} data-testid="cv-photo-remove-button" className="text-[11px] text-zinc-500 hover:text-[#E53935]">
                  Foto verwijderen
                </button>
              )}
              <div className="text-[10px] text-zinc-400 mt-1 text-center max-w-[10rem]">JPG/PNG/WebP, max 5MB. Wordt automatisch verkleind.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CVField label="Naam" value={cv.personal_info.name} onChange={(v) => updatePI({ name: v })} testid="cv-pi-name" readOnly={readOnly} />
            <CVField label="Functietitel" value={cv.personal_info.title} onChange={(v) => updatePI({ title: v })} testid="cv-pi-title" readOnly={readOnly} />
            <CVField label="Adres" value={cv.personal_info.address_line} onChange={(v) => updatePI({ address_line: v })} testid="cv-pi-address" readOnly={readOnly} />
            <CVField label="Postcode + plaats" value={cv.personal_info.postal_city} onChange={(v) => updatePI({ postal_city: v })} testid="cv-pi-postal" readOnly={readOnly} />
            <CVField label="Telefoon" value={cv.personal_info.phone} onChange={(v) => updatePI({ phone: v })} testid="cv-pi-phone" readOnly={readOnly} />
            <CVField label="E-mail" value={cv.personal_info.email} onChange={(v) => updatePI({ email: v })} testid="cv-pi-email" readOnly={readOnly} />
            <CVField label="LinkedIn (URL of handle)" value={cv.personal_info.linkedin} onChange={(v) => updatePI({ linkedin: v })} testid="cv-pi-linkedin" readOnly={readOnly} />
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Profielschets" testid="cv-section-summary" hint="Korte introductie (2-4 zinnen) over wie je bent en wat je biedt.">
        <textarea
          value={cv.summary}
          onChange={(e) => update({ summary: e.target.value })}
          rows={4}
          readOnly={readOnly}
          data-testid="cv-summary-input"
          className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-[15px] rounded-sm bg-white leading-relaxed"
        />
      </SectionCard>

      {/* Brain type promo block (read-only) — only when a brain type is set.
          Placed below the profile sketch so the manual profile leads, with
          the brain-type promo reinforcing it. */}
      {brainTypeLabel && (
        <section className="neuro-card-bold p-6 bg-white" data-testid="cv-section-brain-type">
          <div className="flex items-start gap-4">
            <div className="inline-flex h-10 w-10 items-center justify-center bg-[#1E88E5] text-white rounded-sm shrink-0">
              <Brain size={20} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Breintype</div>
              <h2 className="font-display text-xl font-bold tracking-tight" data-testid="cv-brain-type-label">{brainTypeLabel}</h2>
              <p className="text-[15px] leading-relaxed text-zinc-700 mt-2" data-testid="cv-brain-type-blurb">{brainTypeBlurb}</p>
              <p className="text-[11px] text-zinc-400 mt-3">Deze tekst staat automatisch in het CV en wordt afgeleid van je vastgestelde breintype.</p>
            </div>
          </div>
        </section>
      )}

      <SectionCard
        title="Werkervaring"
        testid="cv-section-work"
        onAdd={readOnly ? null : () => addItem("work_experience", { job_title: "", company: "", location: "", start_date: "", end_date: "", achievements: [{ situation: "", task: "", action: "", result: "", rendered: "" }], responsibilities: [] })}
        addLabel="Werkervaring toevoegen"
        hint="Voer per functie je prestaties in via de STAR-methode (Situatie–Taak–Actie–Resultaat). De AI maakt er een nette CV-zin van."
      >
        {cv.work_experience.length === 0 && <EmptySection label="Nog geen werkervaring toegevoegd." />}
        {cv.work_experience.map((w, i) => (
          <RepeatingBlock key={`we-${i}`} onRemove={readOnly ? null : () => removeItem("work_experience", i)} testid={`cv-work-block-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CVField label="Functietitel" value={w.job_title} onChange={(v) => updateList("work_experience", i, { job_title: v })} testid={`cv-work-${i}-title`} readOnly={readOnly} />
              <CVField label="Bedrijf" value={w.company} onChange={(v) => updateList("work_experience", i, { company: v })} testid={`cv-work-${i}-company`} readOnly={readOnly} />
              <CVField label="Plaats" value={w.location} onChange={(v) => updateList("work_experience", i, { location: v })} testid={`cv-work-${i}-location`} readOnly={readOnly} />
              <div className="grid grid-cols-2 gap-2">
                <CVField label="Van" value={w.start_date} placeholder="Mei 2018" onChange={(v) => updateList("work_experience", i, { start_date: v })} testid={`cv-work-${i}-start`} readOnly={readOnly} />
                <CVField label="Tot" value={w.end_date} placeholder="heden" onChange={(v) => updateList("work_experience", i, { end_date: v })} testid={`cv-work-${i}-end`} readOnly={readOnly} />
              </div>
            </div>
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">STAR-prestaties</label>
                {!readOnly && (
                  <button onClick={() => addAch(i)} data-testid={`cv-work-${i}-add-ach`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-700 hover:text-[#1E88E5]">
                    <Plus size={12} /> Prestatie toevoegen
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {(w.achievements || []).map((ach, j) => (
                  <STARAchievementBlock
                    key={`ach-${j}`}
                    achievement={ach}
                    onChange={(patch) => updateAch(i, j, patch)}
                    onRemove={readOnly ? null : () => removeAch(i, j)}
                    onRewrite={async () => {
                      try {
                        const { data } = await api.post(
                          `/coachees/${coacheeId}/cv/star-rewrite`,
                          {
                            situation: ach.situation || "",
                            task: ach.task || "",
                            action: ach.action || "",
                            result: ach.result || "",
                            job_title: w.job_title || "",
                            company: w.company || "",
                          },
                          { timeout: 60000 }
                        );
                        updateAch(i, j, { rendered: data.rendered });
                      } catch (err) {
                        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                      }
                    }}
                    testidPrefix={`cv-work-${i}-ach-${j}`}
                    readOnly={readOnly}
                  />
                ))}
                {(w.achievements || []).length === 0 && (
                  <div className="text-xs text-zinc-400 italic px-3 py-2 border border-dashed border-zinc-200 rounded-sm">
                    Nog geen prestaties — klik "Prestatie toevoegen" om er één in te voeren via de STAR-methode.
                  </div>
                )}
              </div>
            </div>
          </RepeatingBlock>
        ))}
      </SectionCard>

      <TagsSection
        title="Vaardigheden"
        testid="cv-section-skills"
        values={cv.skills}
        onChange={(v) => update({ skills: v })}
        placeholder="bijv. Microsoft 365, Python, Communicatief"
        readOnly={readOnly}
      />

      <SectionCard
        title="Opleidingen"
        testid="cv-section-education"
        onAdd={readOnly ? null : () => addItem("education", { institution: "", degree: "", start_date: "", end_date: "" })}
        addLabel="Opleiding toevoegen"
      >
        {cv.education.length === 0 && <EmptySection label="Nog geen opleidingen toegevoegd." />}
        {cv.education.map((ed, i) => (
          <RepeatingBlock key={`ed-${i}`} onRemove={readOnly ? null : () => removeItem("education", i)} testid={`cv-edu-block-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CVField label="Instituut / school" value={ed.institution} onChange={(v) => updateList("education", i, { institution: v })} testid={`cv-edu-${i}-institution`} readOnly={readOnly} />
              <CVField label="Opleiding / diploma" value={ed.degree} onChange={(v) => updateList("education", i, { degree: v })} testid={`cv-edu-${i}-degree`} readOnly={readOnly} />
              <CVField label="Van" value={ed.start_date} placeholder="Sep 2014" onChange={(v) => updateList("education", i, { start_date: v })} testid={`cv-edu-${i}-start`} readOnly={readOnly} />
              <CVField label="Tot" value={ed.end_date} placeholder="Jun 2018" onChange={(v) => updateList("education", i, { end_date: v })} testid={`cv-edu-${i}-end`} readOnly={readOnly} />
            </div>
          </RepeatingBlock>
        ))}
      </SectionCard>

      <SectionCard
        title="Talen"
        testid="cv-section-languages"
        onAdd={readOnly ? null : () => addItem("languages", { language: "", proficiency: "" })}
        addLabel="Taal toevoegen"
      >
        {cv.languages.length === 0 && <EmptySection label="Nog geen talen toegevoegd." />}
        {cv.languages.map((l, i) => (
          <RepeatingBlock key={`lng-${i}`} onRemove={readOnly ? null : () => removeItem("languages", i)} testid={`cv-lang-block-${i}`} compact>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CVField label="Taal" value={l.language} placeholder="Nederlands" onChange={(v) => updateList("languages", i, { language: v })} testid={`cv-lang-${i}-name`} readOnly={readOnly} />
              <CVField label="Niveau" value={l.proficiency} placeholder="Moedertaal / Vloeiend / Goed" onChange={(v) => updateList("languages", i, { proficiency: v })} testid={`cv-lang-${i}-proficiency`} readOnly={readOnly} />
            </div>
          </RepeatingBlock>
        ))}
      </SectionCard>

      <TagsSection
        title="Interesses"
        testid="cv-section-interests"
        values={cv.interests}
        onChange={(v) => update({ interests: v })}
        placeholder="bijv. Wielrennen"
        readOnly={readOnly}
      />

      {/* AI Analysis */}
      <SectionCard title="AI-analyse" testid="cv-section-ai">
        <p className="text-sm text-zinc-600 mb-4">
          Laat een AI je CV nakijken en concrete verbeterpunten suggereren. De analyse wordt automatisch opgeslagen.
        </p>
        {cv.ai_feedback && !editingAI ? (
          <div
            className="border border-zinc-200 rounded-sm bg-zinc-50/40 px-5 py-4 cv-markdown"
            data-testid="cv-ai-feedback-rendered"
          >
            <ReactMarkdown
              components={{
                h1: ({ node, ...p }) => <h1 className="font-display text-2xl font-bold tracking-tight mt-2 mb-3 text-zinc-900" {...p} />,
                h2: ({ node, ...p }) => <h2 className="font-display text-xl font-bold tracking-tight mt-5 mb-2 text-zinc-900 border-b border-zinc-200 pb-1" {...p} />,
                h3: ({ node, ...p }) => <h3 className="font-display text-base font-bold tracking-tight mt-4 mb-1.5 text-zinc-800" {...p} />,
                h4: ({ node, ...p }) => <h4 className="font-display text-sm font-bold tracking-tight mt-3 mb-1 text-zinc-800 uppercase" {...p} />,
                p: ({ node, ...p }) => <p className="text-[14.5px] leading-relaxed text-zinc-700 my-2" {...p} />,
                ul: ({ node, ...p }) => <ul className="list-disc pl-5 my-2 space-y-1 text-[14.5px] text-zinc-700" {...p} />,
                ol: ({ node, ...p }) => <ol className="list-decimal pl-5 my-2 space-y-1 text-[14.5px] text-zinc-700" {...p} />,
                li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
                strong: ({ node, ...p }) => <strong className="font-semibold text-zinc-900" {...p} />,
                em: ({ node, ...p }) => <em className="italic" {...p} />,
                hr: () => <hr className="my-4 border-zinc-200" />,
                code: ({ node, ...p }) => <code className="bg-zinc-100 text-zinc-900 px-1 py-0.5 rounded text-[13px] font-mono" {...p} />,
                blockquote: ({ node, ...p }) => <blockquote className="border-l-4 border-[#1E88E5] pl-4 italic text-zinc-600 my-3" {...p} />,
                a: ({ node, ...p }) => <a className="text-[#1E88E5] underline" target="_blank" rel="noreferrer" {...p} />,
              }}
            >
              {cv.ai_feedback}
            </ReactMarkdown>
          </div>
        ) : cv.ai_feedback && editingAI ? (
          <textarea
            value={cv.ai_feedback}
            onChange={(e) => update({ ai_feedback: e.target.value })}
            readOnly={readOnly}
            rows={14}
            data-testid="cv-ai-feedback"
            className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-4 py-3 text-[13.5px] font-mono leading-relaxed rounded-sm bg-zinc-50/40 whitespace-pre-wrap"
          />
        ) : (
          <div className="border border-dashed border-zinc-300 rounded-sm px-5 py-8 text-center text-sm text-zinc-400" data-testid="cv-ai-feedback-empty">
            {readOnly
              ? "Deze coachee heeft nog geen AI-analyse van het CV gemaakt."
              : "Hier verschijnt het advies van de AI nadat je op de knop hieronder klikt."}
          </div>
        )}
        {!readOnly && (
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <button
              onClick={runAI}
              disabled={analyzing}
              data-testid="cv-ai-analyze-button"
              className="inline-flex items-center gap-2 bg-[#1E88E5] text-white px-5 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1976D2] disabled:opacity-60 transition-colors"
            >
              {analyzing ? <ArrowsClockwise size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
              {analyzing ? "Bezig met analyseren..." : "Doe AI-analyse CV"}
            </button>
            {cv.ai_feedback && (
              <button
                type="button"
                onClick={() => setEditingAI((v) => !v)}
                data-testid="cv-ai-edit-toggle"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest border border-zinc-300 px-3 py-1.5 hover:border-zinc-900 transition-colors rounded-sm"
              >
                {editingAI ? <><Check size={12} /> Klaar met bewerken</> : <><PencilSimple size={12} /> Bewerken</>}
              </button>
            )}
            <span className="text-xs text-zinc-500">
              {analyzing ? "Het kan 30-90 seconden duren. Je kunt rustig wachten." : "Duurt circa 30-60 seconden."}
            </span>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable sub-components
// ---------------------------------------------------------------------------
function SectionCard({ title, testid, children, onAdd, addLabel, hint }) {
  return (
    <section className="neuro-card-bold p-6 bg-white" data-testid={testid}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
          {hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}
        </div>
        {onAdd && (
          <button onClick={onAdd} data-testid={`${testid}-add`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest border border-zinc-300 px-3 py-1.5 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors rounded-sm">
            <Plus size={12} /> {addLabel || "Toevoegen"}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function RepeatingBlock({ children, onRemove, testid, compact = false }) {
  return (
    <div className={`relative border-l-4 border-[#1E88E5]/40 pl-4 ${compact ? "py-2" : "py-3"} mb-4`} data-testid={testid}>
      {children}
      {onRemove && (
        <button onClick={onRemove} data-testid={`${testid}-remove`} className="absolute top-2 right-0 text-zinc-400 hover:text-[#E53935] p-1.5">
          <Trash size={16} />
        </button>
      )}
    </div>
  );
}

function EmptySection({ label }) {
  return <div className="text-sm text-zinc-400 italic py-2">{label}</div>;
}

function CVField({ label, value, onChange, testid, placeholder, readOnly }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-[14px] rounded-sm bg-white"
      />
    </div>
  );
}

function TagsSection({ title, testid, values, onChange, placeholder, readOnly }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...values, v]);
    setInput("");
  };
  const remove = (idx) => onChange(values.filter((_, i) => i !== idx));
  return (
    <SectionCard title={title} testid={testid}>
      <div className="flex flex-wrap gap-2 mb-3" data-testid={`${testid}-tags`}>
        {values.length === 0 && <span className="text-sm text-zinc-400 italic">Nog niets toegevoegd.</span>}
        {values.map((v, i) => (
          <span key={`${v}-${i}`} className="inline-flex items-center gap-1.5 bg-zinc-900 text-white px-3 py-1 text-sm rounded-sm" data-testid={`${testid}-tag-${i}`}>
            {v}
            {!readOnly && (
              <button onClick={() => remove(i)} data-testid={`${testid}-tag-${i}-remove`} className="hover:text-[#E53935]"><X size={12} /></button>
            )}
          </span>
        ))}
      </div>
      {!readOnly && (
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder={placeholder}
            data-testid={`${testid}-input`}
            className="flex-1 border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-sm rounded-sm bg-white"
          />
          <button onClick={add} data-testid={`${testid}-add-tag`} className="inline-flex items-center gap-1.5 bg-zinc-900 text-white px-4 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors">
            <Plus size={14} /> Toevoegen
          </button>
        </div>
      )}
    </SectionCard>
  );
}

function STARAchievementBlock({ achievement, onChange, onRemove, onRewrite, testidPrefix, readOnly }) {
  const [expanded, setExpanded] = useState(!achievement.rendered);
  const [rewriting, setRewriting] = useState(false);
  const hasAnySTAR = Boolean(
    achievement.situation || achievement.task || achievement.action || achievement.result
  );

  const handleRewrite = async () => {
    if (!onRewrite) return;
    setRewriting(true);
    try { await onRewrite(); } finally { setRewriting(false); }
  };

  return (
    <div className="border border-zinc-200 rounded-sm bg-white" data-testid={testidPrefix}>
      <div className="flex items-start gap-2 px-3 py-2.5 bg-zinc-50/60 border-b border-zinc-200">
        <span className="text-[#1E88E5] mt-1.5 shrink-0">•</span>
        <div className="flex-1 min-w-0">
          {achievement.rendered ? (
            <input
              value={achievement.rendered}
              onChange={(e) => onChange({ rendered: e.target.value })}
              readOnly={readOnly}
              data-testid={`${testidPrefix}-rendered`}
              className="w-full bg-transparent outline-none text-[14.5px] leading-snug border-b border-transparent focus:border-zinc-400"
            />
          ) : (
            <span className="text-sm text-zinc-400 italic">
              {hasAnySTAR ? "Klik 'Herschrijf met AI' om een nette CV-zin te maken" : "Vul de STAR-onderdelen in"}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`${testidPrefix}-toggle`}
          className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-900 px-2"
        >
          {expanded ? "Inklappen" : "STAR"}
        </button>
        {onRemove && (
          <button type="button" onClick={onRemove} data-testid={`${testidPrefix}-remove`} className="text-zinc-400 hover:text-[#E53935] p-1">
            <X size={14} />
          </button>
        )}
      </div>

      {expanded && (
        <div className="p-3 space-y-2.5" data-testid={`${testidPrefix}-form`}>
          <STARField label="Situatie" placeholder="Wat was de context?" value={achievement.situation} onChange={(v) => onChange({ situation: v })} testid={`${testidPrefix}-situation`} readOnly={readOnly} />
          <STARField label="Taak" placeholder="Wat moest jij doen / bereiken?" value={achievement.task} onChange={(v) => onChange({ task: v })} testid={`${testidPrefix}-task`} readOnly={readOnly} />
          <STARField label="Actie" placeholder="Wat heb je concreet ondernomen?" value={achievement.action} onChange={(v) => onChange({ action: v })} testid={`${testidPrefix}-action`} readOnly={readOnly} />
          <STARField label="Resultaat" placeholder="Wat was het meetbare resultaat?" value={achievement.result} onChange={(v) => onChange({ result: v })} testid={`${testidPrefix}-result`} readOnly={readOnly} />
          {!readOnly && (
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={handleRewrite}
                disabled={!hasAnySTAR || rewriting}
                data-testid={`${testidPrefix}-rewrite`}
                className="inline-flex items-center gap-2 bg-[#1E88E5] text-white px-3 py-1.5 text-xs font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1976D2] disabled:opacity-50 transition-colors"
              >
                {rewriting ? <ArrowsClockwise size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
                {achievement.rendered ? "Herschrijf met AI" : "Genereer CV-zin"}
              </button>
              <span className="text-[11px] text-zinc-500">AI verwerkt STAR tot één bullet (~5s).</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function STARField({ label, placeholder, value, onChange, testid, readOnly }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 pt-1.5">{label}</label>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        placeholder={placeholder}
        rows={1}
        data-testid={testid}
        className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-2 py-1 text-[13.5px] rounded-sm bg-white resize-y min-h-[28px]"
      />
    </div>
  );
}

